import { Box, Grid } from '@mui/material';
import BackButton from '@apigrowfinance/pbolt-back-button';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import transactionClient from '@clients/transactionsClient';
import LoadingError from '@components/loadingError/LoadingError';
import TransactionAuthDetails from '@components/transactions/transactionAuthDetails/TransactionAuthDetails';
import TransactionBreakdownDetails from '@components/transactions/transactionBreakdownDetails/TransactionBreakdownDetails';
import TransactionDetails from '@components/transactions/transactionDetails/TransactionDetails';
import TransactionDetailsHeader from '@components/transactions/transactionDetailsHeader/TransactionDetailsHeader';
import TransactionMerchantDetails from '@components/transactions/transactionMerchantDetails/TransactionMerchantDetails';
import useGetDisputeDetails from '@hooks/queries/useGetDisputeDetails';
import notifyAPIError from '@utils/notifyAPIError';

const { getTransaction } = transactionClient();

const AccountTransactionDetails = () => {
    const { accountId, transactionId } = useParams();
    const { theme } = useTheme();
    const navigate = useNavigate();

    const {
        data: transaction,
        isError,
        isLoading,
    } = useQuery(
        ['transactionsDetails', transactionId],
        () =>
            getTransaction({
                accountId: +accountId,
                transactionId: transactionId,
            }),
        {
            onError: notifyAPIError,
        },
    );

    const { data: dispute, isLoading: disputeLoading } = useGetDisputeDetails(
        transaction,
        Number(transactionId),
    );

    const showAmountAndTaxes =
        transaction?.tax?.length > 0 || transaction?.amount?.length > 1;

    return (
        <Grid container flexDirection="column">
            <Grid item xs={12}>
                <BackButton
                    text="Back"
                    iconSize={theme.spacing(2.75)}
                    buttonOptions={{
                        sx: {
                            mb: 2.875,
                            textTransform: 'none',
                            p: 0,
                            justifyContent: 'flex-start',
                        },
                    }}
                    textOptions={{ variant: 'overline' }}
                    onClick={() => {
                        navigate(-1);
                    }}
                />
            </Grid>
            {isError ? (
                <Box sx={{ mt: 10 }}>
                    <LoadingError />
                </Box>
            ) : (
                <>
                    <Grid
                        container
                        item
                        xs={12}
                        sx={{
                            bgcolor: theme?.palette?.background?.paper,
                            pt: 4.375,
                            pl: 2.625,
                            borderTopLeftRadius: theme.shape.borderRadius,
                            borderTopRightRadius: theme.shape.borderRadius,
                            py: 4,
                            px: 3,
                            mb: 0.25,
                        }}>
                        <TransactionDetailsHeader
                            transaction={transaction}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <TransactionDetails
                        transaction={transaction}
                        isLoading={isLoading}
                        dispute={dispute}
                        disputeLoading={disputeLoading}
                    />
                    {transaction?.merchant && (
                        <TransactionMerchantDetails
                            merchant={transaction?.merchant}
                            isLoading={isLoading}
                        />
                    )}
                    {showAmountAndTaxes && (
                        <TransactionBreakdownDetails
                            isLoading={isLoading}
                            amount={transaction?.amount}
                            tax={transaction?.tax}
                        />
                    )}
                    {transaction?.authorization && (
                        <TransactionAuthDetails
                            authorization={transaction?.authorization}
                            isLoading={isLoading}
                        />
                    )}
                </>
            )}
        </Grid>
    );
};

export default AccountTransactionDetails;
